import React, { useState } from "react";

const ROLE_LABEL = { 1: "Admin", 2: "Operator", 3: "Guest" };

export default function Nav({
  activeView,
  onSwitch,
  alertBadge,
  pm2Badge,
  user,
  role,
  onLogout,
  onManageUsers,
  isAdmin,
}) {
  const [accountOpen, setAccountOpen] = useState(false);

  const displayName = user ? user.displayName || user.name : "Guest";
  const initials = displayName.slice(0, 2).toUpperCase();
  const userRoleText = ROLE_LABEL[role] || "Guest";

  // Cấu trúc danh mục tabs
  const monitoringTabs = [
    { id: 0, label: "Overview", icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" rx="1.5"></rect>
        <rect x="14" y="3" width="7" height="7" rx="1.5"></rect>
        <rect x="14" y="14" width="7" height="7" rx="1.5"></rect>
        <rect x="3" y="14" width="7" height="7" rx="1.5"></rect>
      </svg>
    )},
    { id: 1, label: "Apps & Alerts", icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"></path>
        <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"></path>
      </svg>
    ), badge: alertBadge, badgeColor: "var(--red-soft)", badgeText: "var(--red)" },
    { id: 2, label: "Controls", icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <line x1="4" y1="8" x2="14" y2="8"></line>
        <line x1="18" y1="8" x2="20" y2="8"></line>
        <circle cx="16" cy="8" r="2"></circle>
        <line x1="4" y1="16" x2="8" y2="16"></line>
        <line x1="12" y1="16" x2="20" y2="16"></line>
        <circle cx="10" cy="16" r="2"></circle>
      </svg>
    ) },
    { id: 3, label: "Processes", icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
        <path d="M3.3 7 12 12l8.7-5"></path>
        <line x1="12" y1="22" x2="12" y2="12"></line>
      </svg>
    ), badge: pm2Badge, badgeColor: "var(--amber-soft)", badgeText: "var(--amber)" }
  ];

  const systemTabs = [
    { id: 4, label: "Deploy", icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"></path>
        <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"></path>
        <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"></path>
      </svg>
    ) }
  ];

  if (isAdmin) {
    systemTabs.push({
      id: 5, label: "Docs", icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
        </svg>
      )
    });
  }

  const renderTab = (tab) => {
    const isSelected = activeView === tab.id;
    const itemStyle = {
      position: "relative",
      display: "flex",
      alignItems: "center",
      gap: "11px",
      padding: "9px 12px",
      borderRadius: "9px",
      fontSize: "13.5px",
      fontWeight: isSelected ? 600 : 500,
      color: isSelected ? "var(--primary)" : "var(--dim)",
      background: isSelected ? "var(--primary-soft)" : "transparent",
      cursor: "pointer",
      textDecoration: "none",
      transition: "all 0.15s ease",
      border: "none",
      textAlign: "left",
      width: "100%",
    };

    return (
      <button
        key={tab.id}
        style={itemStyle}
        onClick={() => onSwitch(tab.id)}
        className="nav-item"
      >
        {isSelected && (
          <span style={{
            position: "absolute",
            left: 0,
            top: "50%",
            transform: "translateY(-50%)",
            width: "3px",
            height: "18px",
            borderRadius: "99px",
            background: "var(--primary)",
          }}></span>
        )}
        <span style={{ display: "flex", alignItems: "center", color: isSelected ? "var(--primary)" : "var(--faint)" }}>
          {tab.icon}
        </span>
        <span style={{ flex: 1 }}>{tab.label}</span>
        {tab.badge > 0 && (
          <span style={{
            minWidth: "20px",
            height: "19px",
            padding: "0 6px",
            borderRadius: "99px",
            background: tab.badgeColor,
            color: tab.badgeText,
            fontSize: "11px",
            fontWeight: 700,
            fontFamily: "var(--font-mono)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
            {tab.badge}
          </span>
        )}
      </button>
    );
  };

  return (
    <aside style={{
      width: "246px",
      flexShrink: 0,
      display: "flex",
      flexDirection: "column",
      background: "var(--sidebar)",
      borderRight: "1px solid var(--border)",
      height: "100%",
    }}>
      {/* Brand Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "11px", padding: "20px 20px 18px" }}>
        <div style={{
          width: "36px",
          height: "36px",
          borderRadius: "10px",
          background: "var(--primary)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 6px 16px -4px var(--primary-soft)",
          color: "#fff",
        }}>
          <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"></path>
            <circle cx="12" cy="12" r="2.7" fill="currentColor" stroke="none"></circle>
          </svg>
        </div>
        <div>
          <div style={{ fontSize: "16px", fontWeight: 800, letterSpacing: "-.3px", color: "var(--text)" }}>Argus</div>
          <div style={{ fontSize: "10.5px", fontWeight: 600, letterSpacing: ".4px", color: "var(--faint)" }}>Observability</div>
        </div>
      </div>
      
      <div style={{ height: "1px", background: "var(--border)", margin: "0 16px 14px" }}></div>
      
      {/* Sidebar Navigation */}
      <nav className="scroll" style={{
        flex: 1,
        overflowY: "auto",
        padding: "0 12px",
        display: "flex",
        flexDirection: "column",
        gap: "2px",
      }}>
        <div style={{ fontSize: "10px", fontWeight: 700, letterSpacing: "1px", color: "var(--faint)", padding: "6px 12px 8px" }}>MONITORING</div>
        {monitoringTabs.map(renderTab)}

        <div style={{ fontSize: "10px", fontWeight: 700, letterSpacing: "1px", color: "var(--faint)", padding: "18px 12px 8px" }}>SYSTEM</div>
        {systemTabs.map(renderTab)}
      </nav>

      {/* Account Info / Footer */}
      <div style={{ padding: "12px", borderTop: "1px solid var(--border)", position: "relative" }}>
        {accountOpen && (
          <div style={{
            position: "absolute",
            left: "12px",
            right: "12px",
            bottom: "68px",
            background: "var(--surface)",
            border: "1px solid var(--border2)",
            borderRadius: "12px",
            padding: "6px",
            boxShadow: "var(--shadow)",
            zIndex: 20,
          }}>
            {isAdmin && onManageUsers && (
              <div 
                onClick={() => {
                  setAccountOpen(false);
                  onManageUsers();
                }} 
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "9px 11px",
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "var(--text)",
                  cursor: "pointer",
                }}
                className="dropdown-item"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                Manage users
              </div>
            )}
            <div 
              onClick={() => {
                setAccountOpen(false);
                onLogout();
              }} 
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "9px 11px",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: 500,
                color: "var(--red)",
                cursor: "pointer",
              }}
              className="dropdown-item-danger"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <path d="M16 17l5-5-5-5M21 12H9"></path>
              </svg>
              Sign out
            </div>
          </div>
        )}
        
        <div 
          onClick={() => setAccountOpen(!accountOpen)} 
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "8px 10px",
            borderRadius: "10px",
            cursor: "pointer",
          }}
          className="account-trigger"
        >
          <div style={{
            width: "34px",
            height: "34px",
            borderRadius: "9px",
            background: "linear-gradient(135deg, var(--primary), var(--blue))",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontWeight: 700,
            fontSize: "13px",
          }}>
            {initials}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: "13px",
              fontWeight: 600,
              color: "var(--text)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}>
              {displayName}
            </div>
            <div style={{ fontSize: "11px", color: "var(--faint)" }}>
              {userRoleText} · Production
            </div>
          </div>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--faint)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 9l4-4 4 4M8 15l4 4 4-4"></path>
          </svg>
        </div>
      </div>
      
      {/* Inline styles for hover effects */}
      <style>{`
        .nav-item:hover {
          background: var(--hover) !important;
          color: var(--text) !important;
        }
        .dropdown-item:hover {
          background: var(--hover) !important;
        }
        .dropdown-item-danger:hover {
          background: var(--red-soft) !important;
        }
        .account-trigger:hover {
          background: var(--hover) !important;
        }
      `}</style>
    </aside>
  );
}
